import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from 'flarum/common/app';
import Component from 'flarum/common/Component';
import Icon from 'flarum/common/components/Icon';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import { clampFocus, clampZoom } from '../util';
/**
 * The "pos-crop" surface: shows the image exactly as it will be displayed
 * (cover fit at the chosen focal point, magnified by the zoom factor around
 * that same point) and lets the user pan it by dragging (mouse/touch via
 * pointer events), zoom with the mouse wheel, or use the keyboard
 * (arrows = pan, +/- = zoom).
 *
 * The percentages map 1:1 to CSS `object-position` / `background-position`
 * and the zoom to `transform: scale()` with `transform-origin` at the focal
 * point — the real cover surfaces use identical CSS, so what you see here is
 * exactly what gets rendered.
 */
export default class FocusDragArea extends Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "natural", {
      width: 0,
      height: 0
    });
    _defineProperty(this, "dragging", false);
    _defineProperty(this, "loaded", false);
    _defineProperty(this, "lastUrl", null);
    _defineProperty(this, "startPointer", {
      x: 0,
      y: 0
    });
    _defineProperty(this, "startFocus", {
      x: 50,
      y: 50
    });
    _defineProperty(this, "rafPending", false);
    _defineProperty(this, "boundMove", this.onPointerMove.bind(this));
    _defineProperty(this, "boundUp", this.onPointerUp.bind(this));
  }
  view() {
    var _this$attrs$zoom;
    const _this$attrs = this.attrs,
      url = _this$attrs.url,
      focusX = _this$attrs.focusX,
      focusY = _this$attrs.focusY,
      circle = _this$attrs.circle,
      height = _this$attrs.height;
    const zoom = clampZoom((_this$attrs$zoom = this.attrs.zoom) != null ? _this$attrs$zoom : 1);
    const x = clampFocus(focusX);
    const y = clampFocus(focusY);

    // A fresh source (upload / media pick) restarts the loading state.
    if (url !== this.lastUrl) {
      this.lastUrl = url;
      this.loaded = false;
      this.natural = {
        width: 0,
        height: 0
      };
    }
    return m("div", {
      className: 'ProfileCover-FocusArea' + (circle ? ' ProfileCover-FocusArea--circle' : '') + (this.dragging ? ' is-dragging' : ''),
      style: height ? {
        height: "".concat(height, "px")
      } : undefined,
      tabindex: "0",
      "aria-label": app.translator.trans('ziven-profile-cover.forum.editor.drag_aria'),
      onpointerdown: this.onPointerDown.bind(this),
      onkeydown: this.onKeyDown.bind(this),
      onwheel: this.onWheel.bind(this)
    }, zoom < 1 &&
    // Zoom-out exposes bands around the image — fill them with a blurred
    // copy, exactly like the real cover/avatar rendering does.
    m("img", {
      className: "ProfileCover-FocusArea-blur",
      src: url,
      alt: "",
      draggable: "false",
      "aria-hidden": "true",
      style: {
        objectPosition: "".concat(x, "% ").concat(y, "%")
      }
    }), m("img", {
      className: "ProfileCover-FocusArea-image",
      src: url,
      alt: "",
      draggable: "false"
      // Jump the browser's request queue: on pages with dozens of post
      // images (HTTP/1.1 = ~6 connections per host) the editor image
      // would otherwise wait many seconds at default priority.
      ,
      fetchpriority: "high",
      decoding: "async",
      style: {
        objectPosition: "".concat(x, "% ").concat(y, "%"),
        transform: "scale(".concat(zoom, ")"),
        transformOrigin: "".concat(x, "% ").concat(y, "%")
      },
      onload: e => {
        const img = e.target;
        this.natural = {
          width: img.naturalWidth,
          height: img.naturalHeight
        };
        this.loaded = true;
        m.redraw();
      }
    }), !this.loaded && m("div", {
      className: "ProfileCover-FocusArea-loading",
      "aria-hidden": "true"
    }, m(LoadingIndicator, null)), circle && m("div", {
      className: "ProfileCover-FocusArea-mask",
      "aria-hidden": "true"
    }), m("div", {
      className: "ProfileCover-FocusArea-grid",
      "aria-hidden": "true"
    }), !circle && m("div", {
      className: "ProfileCover-FocusArea-crosshair",
      style: {
        left: "".concat(x, "%"),
        top: "".concat(y, "%")
      },
      "aria-hidden": "true"
    }), m("div", {
      className: "ProfileCover-FocusArea-hint",
      "aria-hidden": "true"
    }, m(Icon, {
      name: "fas fa-arrows-alt"
    }), " ", app.translator.trans('ziven-profile-cover.forum.editor.drag_hint')));
  }
  onremove(vnode) {
    super.onremove(vnode);
    this.unbindWindow();
  }

  /**
   * SIGNED pan range per axis: rendered size (cover fit × zoom) minus the
   * container size. Positive = the image overflows and panning slides the
   * viewport over it; negative = the image is smaller (zoom-out) and panning
   * slides the image inside the window. The same drag formula handles both
   * signs; ~zero means the axis cannot be panned.
   */
  panRange() {
    var _this$element, _this$attrs$zoom2;
    const box = (_this$element = this.element) == null ? void 0 : _this$element.getBoundingClientRect();
    if (!box || !this.natural.width || !this.natural.height) {
      return {
        x: 0,
        y: 0
      };
    }
    const zoom = clampZoom((_this$attrs$zoom2 = this.attrs.zoom) != null ? _this$attrs$zoom2 : 1);
    const scale = Math.max(box.width / this.natural.width, box.height / this.natural.height) * zoom;
    return {
      x: this.natural.width * scale - box.width,
      y: this.natural.height * scale - box.height
    };
  }
  onPointerDown(e) {
    // Only main button / touch.
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    this.dragging = true;
    this.startPointer = {
      x: e.clientX,
      y: e.clientY
    };
    this.startFocus = {
      x: this.attrs.focusX,
      y: this.attrs.focusY
    };
    window.addEventListener('pointermove', this.boundMove);
    window.addEventListener('pointerup', this.boundUp);
    window.addEventListener('pointercancel', this.boundUp);
    m.redraw();
  }
  onPointerMove(e) {
    if (!this.dragging) return;
    const range = this.panRange();
    const dx = e.clientX - this.startPointer.x;
    const dy = e.clientY - this.startPointer.y;

    // Overflowing axis (range > 0): dragging the image right reveals more of
    // its left side → focus % shrinks. Zoomed-out axis (range < 0): dragging
    // right moves the floating image right → focus % grows. Both cases reduce
    // to the same signed formula.
    const x = Math.abs(range.x) > 1 ? this.startFocus.x - dx / range.x * 100 : this.startFocus.x;
    const y = Math.abs(range.y) > 1 ? this.startFocus.y - dy / range.y * 100 : this.startFocus.y;
    this.attrs.onchange(clampFocus(x), clampFocus(y));
    this.scheduleRedraw();
  }
  onPointerUp() {
    this.dragging = false;
    this.unbindWindow();
    m.redraw();
  }
  unbindWindow() {
    window.removeEventListener('pointermove', this.boundMove);
    window.removeEventListener('pointerup', this.boundUp);
    window.removeEventListener('pointercancel', this.boundUp);
  }
  onWheel(e) {
    var _this$attrs$zoom3;
    if (!this.attrs.onzoom) return;
    e.preventDefault();
    const current = clampZoom((_this$attrs$zoom3 = this.attrs.zoom) != null ? _this$attrs$zoom3 : 1);
    const step = e.deltaY < 0 ? 0.1 : -0.1;
    this.attrs.onzoom(clampZoom(current + step));
    this.scheduleRedraw();
  }
  onKeyDown(e) {
    const step = e.shiftKey ? 10 : 2;
    let _this$attrs2 = this.attrs,
      x = _this$attrs2.focusX,
      y = _this$attrs2.focusY;
    switch (e.key) {
      case 'ArrowLeft':
        x -= step;
        break;
      case 'ArrowRight':
        x += step;
        break;
      case 'ArrowUp':
        y -= step;
        break;
      case 'ArrowDown':
        y += step;
        break;
      case '+':
      case '=':
        if (this.attrs.onzoom) {
          var _this$attrs$zoom4;
          e.preventDefault();
          this.attrs.onzoom(clampZoom(clampZoom((_this$attrs$zoom4 = this.attrs.zoom) != null ? _this$attrs$zoom4 : 1) + 0.1));
        }
        return;
      case '-':
      case '_':
        if (this.attrs.onzoom) {
          var _this$attrs$zoom5;
          e.preventDefault();
          this.attrs.onzoom(clampZoom(clampZoom((_this$attrs$zoom5 = this.attrs.zoom) != null ? _this$attrs$zoom5 : 1) - 0.1));
        }
        return;
      default:
        return;
    }
    e.preventDefault();
    this.attrs.onchange(clampFocus(x), clampFocus(y));
  }
  scheduleRedraw() {
    if (this.rafPending) return;
    this.rafPending = true;
    requestAnimationFrame(() => {
      this.rafPending = false;
      m.redraw();
    });
  }
}
flarum.reg.add('ziiven-profile-cover', 'common/components/FocusDragArea', FocusDragArea);